import React, { useEffect, useState, useCallback } from 'react';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { FaChartPie, FaChartLine, FaChartBar } from "react-icons/fa";
import '../../../css/UserAdmin/AnalyticsSection.css';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658'];

const QueueAnalytics = ({ department, allowedDepartments, hasAnalyticsAccess }) => {
  const [analyticsData, setAnalyticsData] = useState({
    departmentPieData: [],
    timeSeriesData: [],
    queueTrendsData: []
  });
  const [selectedTimeframe, setSelectedTimeframe] = useState('7days');
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);

  const generateSampleAnalyticsData = useCallback(() => {
    console.log("Generating sample analytics data for departments:", allowedDepartments);
    const pieData = allowedDepartments.map((dept) => ({
      name: dept,
      value: Math.floor(Math.random() * 100) + 50,
    }));
    const timeSeries = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dayData = {
        date: date.toISOString().split('T')[0],
        completed: Math.floor(Math.random() * 30) + 10,
        pending: Math.floor(Math.random() * 10) + 2,
        total: 0
      };
      dayData.total = dayData.completed + dayData.pending;
      timeSeries.push(dayData);
    }
    const queueTrends = [];
    for (let hour = 8; hour <= 17; hour++) {
      queueTrends.push({
        hour: `${String(hour).padStart(2, '0')}:00`,
        visitors: Math.floor(Math.random() * 15) + 5
      });
    }
    setAnalyticsData({ departmentPieData: pieData, timeSeriesData: timeSeries, queueTrendsData: queueTrends });
  }, [allowedDepartments]);

  const processAnalyticsData = useCallback((dataFromApi) => {
    const pieData = dataFromApi.map((item) => ({
      name: item.department,
      value: item.totalServed || 0,
    }));
    const aggregatedTimeSeries = {};
    dataFromApi.forEach(deptData => {
      (deptData.timeSeries || []).forEach(ts => {
        if (!aggregatedTimeSeries[ts.date]) {
          aggregatedTimeSeries[ts.date] = { date: ts.date, completed: 0, pending: 0, total: 0 };
        }
        aggregatedTimeSeries[ts.date].completed += (ts.completed || 0);
        aggregatedTimeSeries[ts.date].pending += (ts.pending || 0);
        aggregatedTimeSeries[ts.date].total = aggregatedTimeSeries[ts.date].completed + aggregatedTimeSeries[ts.date].pending;
      });
    });
    const timeSeriesData = Object.values(aggregatedTimeSeries).sort((a, b) => new Date(a.date) - new Date(b.date));
    const aggregatedQueueTrends = {};
    dataFromApi.forEach(deptData => {
      (deptData.hourlyTrends || []).forEach(ht => {
        if (!aggregatedQueueTrends[ht.hour]) {
          aggregatedQueueTrends[ht.hour] = { hour: ht.hour, visitors: 0 };
        }
        aggregatedQueueTrends[ht.hour].visitors += (ht.visitors || 0);
      });
    });
    const queueTrendsData = Object.values(aggregatedQueueTrends).sort((a, b) => parseInt(a.hour) - parseInt(b.hour));
    setAnalyticsData({ departmentPieData: pieData, timeSeriesData, queueTrendsData });
  }, []);

  const fetchAnalyticsData = useCallback(async () => {
    if (!hasAnalyticsAccess || !allowedDepartments || allowedDepartments.length === 0) {
      setAnalyticsData({ departmentPieData: [], timeSeriesData: [], queueTrendsData: [] });
      return;
    }
    setLoadingAnalytics(true);
    try {
      const getDateRange = () => {
        const end = new Date();
        const start = new Date();
        switch (selectedTimeframe) {
          case '7days': start.setDate(start.getDate() - 7); break;
          case '30days': start.setDate(start.getDate() - 30); break;
          case '90days': start.setDate(start.getDate() - 90); break;
          case '1year': start.setFullYear(start.getFullYear() - 1); break;
          default: start.setDate(start.getDate() - 7);
        }
        return { startDate: start.toISOString().split('T')[0], endDate: end.toISOString().split('T')[0] };
      };
      const { startDate, endDate } = getDateRange();
      const API_URL = process.env.REACT_APP_API_URL;
      if (!API_URL) throw new Error("API URL is not configured.");

      const departmentPromises = allowedDepartments.map(async (dept) => {
        const response = await fetch(
          `${API_URL}/api/admin/guestQueueData/analytics?department=${encodeURIComponent(dept)}&startDate=${startDate}&endDate=${endDate}`
        );
        if (response.ok) return { department: dept, ...(await response.json()) };
        console.warn(`Failed to fetch analytics for ${dept}. Status: ${response.status}`);
        return null;
      });
      const results = await Promise.all(departmentPromises);
      const validResults = results.filter(result => result !== null);

      if (validResults.length === 0 && allowedDepartments.length > 0) {
        console.warn("No valid analytics data from API; generating sample data.");
        generateSampleAnalyticsData();
      } else if (validResults.length > 0) {
        processAnalyticsData(validResults);
      } else {
        setAnalyticsData({ departmentPieData: [], timeSeriesData: [], queueTrendsData: [] });
      }
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      generateSampleAnalyticsData();
    } finally {
      setLoadingAnalytics(false);
    }
  }, [hasAnalyticsAccess, allowedDepartments, selectedTimeframe, generateSampleAnalyticsData, processAnalyticsData]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  if (!hasAnalyticsAccess) {
    return null;
  }

  const pieChartAvailable = analyticsData.departmentPieData && analyticsData.departmentPieData.length > 0;
  const barChartAvailable = analyticsData.queueTrendsData && analyticsData.queueTrendsData.length > 0;
  const lineChartAvailable = analyticsData.timeSeriesData && analyticsData.timeSeriesData.length > 0;
  const anyChartAvailable = pieChartAvailable || barChartAvailable || lineChartAvailable;

  return (
    // --- MODIFICATION IS HERE ---
    <div className="analytics-section queue-analytics">
      <div className="analytics-header">
        <h3 style={{ margin: 0, color: '#2c3e50', fontSize: '16px' }}>Queue Performance Overview</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label htmlFor="academic-year-selector">Time Period:</label>
          <select
            className="timeframe-selector"
            value={selectedTimeframe}
            onChange={(e) => setSelectedTimeframe(e.target.value)}
            disabled={loadingAnalytics}
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="90days">Last 3 Months</option>
            <option value="1year">Last Year</option>
          </select>
        </div>
      </div>

      {loadingAnalytics ? (
        <div className="loading-analytics-indicator">Loading analytics data...</div>
      ) : !anyChartAvailable ? (
        <div className="chart-card no-data-message">
          No queue data available for the selected timeframe and departments.
        </div>
      ) : (
        <div className="charts-row-container">
          {pieChartAvailable && (
            <div className="chart-card">
              <h3 className="chart-title"><FaChartPie /> Department Volume</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={analyticsData.departmentPieData}
                    cx="50%" cy="50%" labelLine={false}
                    label={({ name, percent, value }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={90}
                    fill="#8884d8" dataKey="value"
                  >
                    {analyticsData.departmentPieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, name]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          {barChartAvailable && (
            <div className="chart-card">
              <h3 className="chart-title"><FaChartBar /> Hourly Visitor Trends</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analyticsData.queueTrendsData} margin={{ top: 20, right: 20, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="visitors" name="Visitors" fill={COLORS[1]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {lineChartAvailable && (
            <div className="chart-card">
              <h3 className="chart-title"><FaChartLine /> Daily Queue Activity</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analyticsData.timeSeriesData} margin={{ top: 20, right: 20, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="completed" name="Completed" stroke={COLORS[1]} strokeWidth={2} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="pending" name="Pending" stroke={COLORS[2]} strokeWidth={2} activeDot={{ r: 6 }} />
                  <Line type="monotone" dataKey="total" name="Total" stroke={COLORS[0]} strokeWidth={2} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QueueAnalytics;